import { Link } from "react-router-dom";
import ContactForm from "./components/ContactForm";
import { useSeo } from "@/hooks/useSeo";

const scopeItems = [
  { icon: "ri-movie-line", title: "影片资料整理", desc: "汇总《饥渴的岳》系列电影的中文字幕版本、年份、地区、类型与评分等基础信息。" },
  { icon: "ri-translate-2", title: "中文字幕索引", desc: "覆盖 BD、4K、DV、合集等多类中文字幕版本，方便按版本快速定位。" },
  { icon: "ri-search-line", title: "检索与筛选", desc: "提供关键词搜索、地区/年份/版本/类型组合筛选与排序，提升查找效率。" },
  { icon: "ri-heart-3-line", title: "收藏与浏览", desc: "收藏状态保存在你的浏览器本地，随时标记想看的影片，刷新后依然保留。" },
];

export default function About() {
  useSeo({
    title: "关于本站 - 饥渴的岳中文字幕",
    description: "了解「饥渴的岳中文字幕」的网站定位、内容范围、使用说明与联系方式，以及版权与免责提示。",
    keywords: "饥渴的岳中文字幕,关于本站,影视资源站,中文字幕网站说明",
  });

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 font-label mb-6 flex-wrap">
        <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-200">关于</span>
      </nav>

      <header className="mb-10 md:mb-14 max-w-3xl">
        <h1 className="font-heading text-foreground-50 text-2xl md:text-4xl font-bold mb-4">
          关于「饥渴的岳中文字幕」
        </h1>
        <p className="text-sm md:text-base text-foreground-300 font-label leading-relaxed">
          我们是一个面向华语观众的影视内容展示站点，围绕《饥渴的岳》系列电影整理中文字幕资源信息。希望通过清晰的分类、扎实的影片资料与顺手的检索体验，让每一位影迷都能更快找到自己需要的中文字幕版本。
        </p>
      </header>

      <section className="mb-12 md:mb-16">
        <h2 className="font-heading text-foreground-50 text-xl md:text-2xl font-bold mb-6 flex items-center gap-3">
          <span className="w-1 h-6 rounded-full bg-accent-500" />
          网站定位
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2 rounded-lg bg-background-900 border border-background-800 p-5 md:p-7">
            <p className="text-sm md:text-base text-foreground-300 font-label leading-relaxed mb-4">
              本站定位为专业的影视内容展示站，核心围绕《饥渴的岳》系列电影的中文字幕资源展开，目标用户是对该系列感兴趣的华语观众，尤其是需要中文字幕支持的影迷朋友。
            </p>
            <p className="text-sm md:text-base text-foreground-300 font-label leading-relaxed">
              我们把用户的核心需求归纳为三件事：高清影视的浏览体验、丰富的剧情与影片信息查询、以及快捷浏览影视资源分类。围绕这三点，站点提供了首页推荐、分类筛选、搜索与详情页等完整浏览路径。
            </p>
          </div>
          <div className="rounded-lg bg-primary-950 border border-primary-900/60 p-5 md:p-7 flex flex-col justify-center">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-heading text-3xl font-bold text-accent-500">32</span>
              <span className="text-foreground-300 font-label text-sm">部演示影片</span>
            </div>
            <p className="text-xs md:text-sm text-foreground-400 font-label leading-relaxed">
              站内影片资料为演示用虚拟内容，涵盖 2020–2026 年间多个中文字幕版本，用于展示完整的浏览与筛选体验。
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12 md:mb-16">
        <h2 className="font-heading text-foreground-50 text-xl md:text-2xl font-bold mb-6 flex items-center gap-3">
          <span className="w-1 h-6 rounded-full bg-accent-500" />
          内容范围与使用说明
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-6">
          {scopeItems.map((item) => (
            <div key={item.title} className="rounded-lg bg-background-900 border border-background-800 p-5">
              <span className="w-11 h-11 rounded-md bg-secondary-900/60 text-accent-400 flex items-center justify-center text-xl mb-4">
                <i className={item.icon} />
              </span>
              <h3 className="font-heading text-foreground-50 font-semibold text-base mb-2">{item.title}</h3>
              <p className="text-sm text-foreground-400 font-label leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-dashed border-background-700 bg-background-900/60 p-5 md:p-6 flex items-start gap-4">
          <span className="w-12 h-12 rounded-full bg-background-800 text-accent-400 flex items-center justify-center text-2xl shrink-0">
            <i className="ri-information-line" />
          </span>
          <div>
            <h3 className="font-heading text-foreground-100 font-semibold text-base mb-2">关于播放演示</h3>
            <p className="text-sm text-foreground-400 font-label leading-relaxed">
              影片详情页中的「可播放资源」区域仅作界面示意，本站暂未接入真实视频播放接口，请勿将其视为可在线观看。后续如获得正版授权，可在该区域基础上接入播放能力。
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12 md:mb-16">
        <h2 className="font-heading text-foreground-50 text-xl md:text-2xl font-bold mb-6 flex items-center gap-3">
          <span className="w-1 h-6 rounded-full bg-accent-500" />
          联系方式
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <ContactForm />
          <aside className="rounded-lg bg-background-900 border border-background-800 p-5 md:p-7">
            <h3 className="font-heading text-foreground-50 font-semibold text-base mb-4">其他联系方式</h3>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-md bg-background-800 text-accent-400 flex items-center justify-center text-lg shrink-0">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500 font-label mb-1">邮箱</p>
                  <p className="text-sm text-foreground-100 font-label break-all">contact@jky-subtitles.demo</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-md bg-background-800 text-accent-400 flex items-center justify-center text-lg shrink-0">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500 font-label mb-1">回复时间</p>
                  <p className="text-sm text-foreground-100 font-label">通常 1–2 个工作日内回复</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-md bg-background-800 text-accent-400 flex items-center justify-center text-lg shrink-0">
                  <i className="ri-chat-smile-2-line" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500 font-label mb-1">反馈范围</p>
                  <p className="text-sm text-foreground-100 font-label">资源建议、内容纠错、合作咨询</p>
                </div>
              </li>
            </ul>
          </aside>
        </div>
      </section>

      <section>
        <h2 className="font-heading text-foreground-50 text-xl md:text-2xl font-bold mb-6 flex items-center gap-3">
          <span className="w-1 h-6 rounded-full bg-accent-500" />
          版权与免责提示
        </h2>
        <div className="rounded-lg bg-primary-950 border border-primary-900/60 p-5 md:p-7 space-y-4">
          <p className="text-sm text-foreground-300 font-label leading-relaxed">
            本站为影视内容展示与信息索引站点，站内影片名称、简介、评分、演职人员等资料均为演示用虚拟内容，不代表任何真实影视作品。
          </p>
          <p className="text-sm text-foreground-300 font-label leading-relaxed">
            本站不提供任何视频文件的存储、上传或在线播放服务，相关区域仅为界面示意。若后续涉及真实影片信息，请以权利方发布的官方内容为准。
          </p>
          <p className="text-sm text-foreground-300 font-label leading-relaxed">
            如权利人认为站内内容存在不当之处，可通过上方联系方式与我们沟通，我们将及时核实并处理。
          </p>
          <div className="pt-2">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 hover:bg-accent-400 text-background-950 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-list-check-2" />
              进入分类页浏览影片
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}